"use client";

import { FC } from "react";
import Link from "next/link";
import { Theme, themeRootClassMap } from "apps/website/maps/Theme.map";
import Container from "apps/website/components/layout/Container/Container";
import Grid from "apps/website/components/layout/Grid/Grid";
import Column from "apps/website/components/layout/Column/Column";
import Image, { IImageProps } from "apps/website/components/base/Image/Image";
import {
  useAnnouncementCarousel,
} from "apps/website/hooks/useAnnouncementCarousel";
import type { IconSvg } from "apps/website/components/base/Icon/Icon.map";

import TextSubtitle from "../../base/Text/TextSubtitle/TextSubtitle";
import Icon from "../../base/Icon/Icon";

export interface IAnnouncement {
  text: string;
  to?: string;
  image?: IImageProps;
  icon?: IconSvg;
}

export interface IAnnouncementBanner {
  announcements: IAnnouncement[];
  theme?: Theme;
  className?: string;
  hideSpacerDiv?: boolean;
  positionClassName?: string;
}

const AnnouncementBanner: FC<IAnnouncementBanner> = ({
  announcements,
  theme = "brand",
  className = "",
  hideSpacerDiv = false,
  positionClassName = "",
}) => {
  const { currentIndex, isFading, isVisible } = useAnnouncementCarousel({
    totalAnnouncements: announcements.length,
  });

  const announcement = announcements[currentIndex];

  const textComponent = (text: string) => (
    <TextSubtitle
      tag="span"
      size={2}
      align="center"
      className="whitespace-nowrap"
      data-testid="AnnouncementBannerText"
    >
      { text }
    </TextSubtitle>
  );

  return <>
    <div
      data-component="AnnouncementBanner"
      className={`z-[45] w-full fixed h-[44px] flex justify-center items-center transition-all duration-500 box-border overflow-hidden ${themeRootClassMap[theme]} ${className} ${positionClassName}`}
      data-theme={theme}
      data-state="open"
    >
      <div>
        <Container>
          <Grid>
            <Column align="center" justify="center">
              { announcement ? (
                <div
                  className={`flex items-center justify-center transition-opacity duration-700 ${isFading ? "opacity-0" : "opacity-100"}`}
                  aria-hidden={isVisible ? "false" : "true"}
                >
                  { (() => {
                    const commonInner = (
                      <>
                        { announcement.icon && (
                          <Icon icon={announcement.icon} size="xsmall" className="mr-1" />
                        ) }
                        { textComponent(announcement.text) }
                        { announcement.image?.src && (
                          <div className="pl-2 w-[100px] hidden lg:block">
                            <Image image={announcement.image} alt={announcement.text} />
                          </div>
                        ) }
                      </>
                    );

                    return announcement.to ? (
                      <Link
                        href={announcement.to}
                        tabIndex={isVisible ? 0 : -1}
                        className="flex items-center justify-center"
                      >
                        { commonInner }
                      </Link>
                    ) : commonInner;
                  })() }
                </div>
              ) : null }
            </Column>
          </Grid>
        </Container>
      </div>
    </div>
    { !hideSpacerDiv && <div className={`h-[44px] w-full ${className}`} /> }
  </>;
};

export default AnnouncementBanner;
